<template>
  <WrapperForm
    :tabs
    v-model="currentTab"
    :customClass="['scrollable', 'scrollbar']"
  >
    <component :is="TABS_COMPONENTS[currentTab]" />
  </WrapperForm>
</template>

<script setup lang="ts">
  import { ref, type Component } from 'vue'
  import { storeToRefs } from 'pinia'
  import type { ContratProADTO } from '@/features/api/extranet/dto/Demande/PROA'
  import { type ErreurSummaryConfiguration } from '@/features/application/erreur-summary'
  import { useDemandeTabsSummarizer } from '@/features/interface/demande/shared'
  import ContratStep from './steps/contrat/ContratStep.vue'
  import DocumentsStep from './steps/documents/DocumentsStep.vue'
  import EmployeurStep from './steps/employeur/EmployeurStep.vue'
  import FormationStep from './steps/formation/FormationStep.vue'
  import IntroductionStep from './steps/introduction/IntroductionStep.vue'
  import SalarieStep from './steps/salarie/SalarieStep.vue'
  import SyntheseStep from './steps/synthese/SyntheseStep.vue'
  import { useDemandePROAStore } from './useDemandePROAStore'

  const TabKeys = ['Introduction', 'Employeur', 'Salarié', 'Contrat', 'Formation', 'Synthèse', 'Documents'] as const
  const currentTab = ref(TabKeys[0])
  type TabKey = (typeof TabKeys)[number]

  const TABS_COMPONENTS: { [key in TabKey]?: Component } = {
    Introduction: IntroductionStep,
    Employeur: EmployeurStep,
    Salarié: SalarieStep,
    Contrat: ContratStep,
    Formation: FormationStep,
    Synthèse: SyntheseStep,
    Documents: DocumentsStep,
  } as const

  const config = {
    Introduction: { include: (state) => [state.validationNotice] },
    Employeur: {
      includeDeep: (state) => [state.employeur],
    },
    Salarié: {
      includeDeep: (state) => [state.salarie, state.tutorat],
    },
    Contrat: {
      includeDeep: (state) => [state.contrat],
      include: (state) => [state.dispositif],
    },
    Formation: { includeDeep: (state) => [state.formation] },
    Synthèse: {
      include: (state) => [state.lieuSignature, state.commentaire, state.acceptationCg, state.attestationSurHonneur],
    },
    Documents: {
      includeDeep: (state) => [state.fichiers],
      include: (state) => [state.attestationPiecesJustificatives],
    },
  } satisfies ErreurSummaryConfiguration<ContratProADTO>

  const { meta } = storeToRefs(useDemandePROAStore())
  const { tabs } = useDemandeTabsSummarizer<ContratProADTO>(config, meta)
</script>
